import { useCallback } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Icon } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import actions from "actions"
import api from "api"
import { IdeaLikeHandler } from "components/lib/Ideas"
import { TooltipButton } from "components/lib/UI"
import { useAppDispatch } from "store"
import util from "utils/utils"

type LikeActionProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
}

const LikeAction = ({ idea }: LikeActionProps) => {
  const { t } = useTranslation()
  const ideaId = idea?._id
  const dispatch = useAppDispatch()

  const onFollow = useCallback(
    (context, id) => {
      dispatch(actions.user.onFollow(context, id))
    },
    [dispatch],
  )

  const voteForIdea = useCallback(() => {
    api.ideas.vote(
      ideaId,
      !idea?.votedFor,
      ({ addedVotesFor, removedVotesFor }) => {
        const newIdea = { ...idea }
        if (!newIdea.voteCount) newIdea.voteCount = 0
        if (addedVotesFor.indexOf(idea._id) > -1) {
          onFollow("challenge", idea.challenge)
          newIdea.votedFor = true
          newIdea.voteCount += 1
        }
        if (removedVotesFor.indexOf(idea._id) > -1) {
          newIdea.votedFor = false
          newIdea.voteCount -= 1
        }
        util.onUpdateIdea(idea, { voteCount: newIdea.voteCount, votedFor: newIdea.votedFor })
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [ideaId, idea, onFollow])

  if (idea.ownerChallenge && idea.ownerChallenge.votingVisibility !== "users") {
    return null
  }

  return (
    <IdeaLikeHandler
      idea={idea}
      challenge={idea.ownerChallenge}
      onLikeIdea={() => voteForIdea()}
      trigger={
        <TooltipButton
          icon
          secondary={idea?.votedFor}
          style={{ position: "relative" }}
          tooltip={idea?.votedFor ? `Un-like ${t("generic.idea")}` : `Like ${t("generic.idea")}`}
        >
          <Icon name="thumbs up" />
          {idea.voteCount && idea.ownerChallenge?.voteCountVisibility === "users" ? (
            <div style={{ position: "absolute", right: 3, bottom: 0, opacity: 0.9, fontSize: 10 }}>
              {idea.voteCount || 0}
            </div>
          ) : null}
        </TooltipButton>
      }
    />
  )
}

export default LikeAction
